/**
 * Lagoon message utilities for safely accessing lagoon data with TypeScript support
 */

export interface LagoonMessage {
  key?: string;
  title?: string;
  message?: string;
  [k: string]: any;
}

/**
 * Safely retrieves a lagoon message with built-in error logging for debugging.
 * Logs a warning in non-production environments when a message key is not found.
 *
 * @param lagoonData - Array of lagoon messages
 * @param key - The key to search for
 * @returns The matching lagoon message or undefined
 *
 * @example
 * ```ts
 * const lagoonData = useLagoon('ui-messaging')();
 * const message = getLagoonMessage(lagoonData, 'MY_MESSAGE_KEY');
 * // Will log warning if key not found (dev only)
 * ```
 */
export const getLagoonMessage = (
  lagoonData: unknown,
  key: string
): LagoonMessage | undefined => {
  if (!lagoonData) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(`[Lagoon] Message key not found: "${key}"`);
    }
    return undefined;
  }

  const result = ((lagoonData as any[]) || []).find(
    (item: any) => item?.key === key
  ) as LagoonMessage | undefined;

  if (!result) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(`[Lagoon] Message key not found: "${key}"`);
    }
  }

  return result;
};
